import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { ChevronDown, ChevronRight, Layers, BookOpen } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const diffColors = {
  easy: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30",
  medium: "bg-amber-500/20 text-amber-400 border-amber-500/30",
  hard: "bg-red-500/20 text-red-400 border-red-500/30",
};

function SubtopicNode({ sub }) {
  const total = sub.question_count;
  return (
    <div
      data-testid={`subtopic-${sub.id}`}
      className="flex items-center justify-between py-2 px-4 ml-8 border-l-2 border-border hover:bg-secondary/30 transition-colors rounded-r-sm"
    >
      <div className="flex items-center gap-2">
        <div className="w-1.5 h-1.5 rounded-full bg-muted-foreground" />
        <span className="text-sm">{sub.name}</span>
      </div>
      <div className="flex items-center gap-2">
        {sub.difficulty.hard > 0 && (
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${diffColors.hard}`}>
            H:{sub.difficulty.hard}
          </Badge>
        )}
        {sub.difficulty.medium > 0 && (
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${diffColors.medium}`}>
            M:{sub.difficulty.medium}
          </Badge>
        )}
        {sub.difficulty.easy > 0 && (
          <Badge variant="outline" className={`text-[10px] px-1.5 py-0 ${diffColors.easy}`}>
            E:{sub.difficulty.easy}
          </Badge>
        )}
        <span className="font-mono text-xs text-muted-foreground w-8 text-right">{total}</span>
      </div>
    </div>
  );
}

function TopicNode({ topic }) {
  const [open, setOpen] = useState(false);
  const total = topic.question_count;
  const hardPct = total > 0 ? (topic.difficulty.hard / total) * 100 : 0;
  const medPct = total > 0 ? (topic.difficulty.medium / total) * 100 : 0;

  return (
    <div data-testid={`topic-${topic.id}`} className="border border-border rounded-sm overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between p-4 hover:bg-secondary/30 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {open ? <ChevronDown size={16} className="text-primary" /> : <ChevronRight size={16} className="text-muted-foreground" />}
          <div>
            <span className="font-medium text-sm">{topic.name}</span>
            <span className="text-xs text-muted-foreground ml-2">({topic.weight}% weight)</span>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-1.5 w-48">
            <div className="flex-1 h-2 bg-secondary rounded-full overflow-hidden flex">
              <div className="h-full bg-red-500/70" style={{ width: `${hardPct}%` }} />
              <div className="h-full bg-amber-500/70" style={{ width: `${medPct}%` }} />
              <div className="h-full bg-emerald-500/70" style={{ width: `${100 - hardPct - medPct}%` }} />
            </div>
          </div>
          <span className="font-mono text-sm font-semibold w-10 text-right">{total}</span>
        </div>
      </button>
      {open && topic.subtopics.length > 0 && (
        <div className="border-t border-border bg-card/50 py-1">
          {topic.subtopics.map(sub => (
            <SubtopicNode key={sub.id} sub={sub} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function TopicTree() {
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/topics/tree`),
      axios.get(`${API}/questions/stats`),
    ]).then(([treeRes, statsRes]) => {
      setData(treeRes.data);
      setStats(statsRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return <div className="p-8 text-muted-foreground">Loading topic tree...</div>;
  }

  const mainBank = stats?.main_bank || {};
  const pyqBank = stats?.pyq_bank || {};

  return (
    <div className="p-8 lg:p-12 space-y-8 animate-fade-in" data-testid="topic-tree-page">
      <div>
        <h1 className="font-heading text-4xl md:text-5xl font-bold tracking-tight">
          Topic Map
        </h1>
        <p className="text-muted-foreground mt-2 text-base">
          Question bank structure and coverage across all UPSC GS-I topics
        </p>
      </div>

      {/* Stats Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Total Questions</p>
            <p className="font-mono text-3xl font-bold mt-1">{mainBank.total || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Hard</p>
            <p className="font-mono text-3xl font-bold mt-1 text-red-400">{mainBank.hard || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">Medium</p>
            <p className="font-mono text-3xl font-bold mt-1 text-amber-400">{mainBank.medium || 0}</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardContent className="p-4">
            <p className="text-xs text-muted-foreground uppercase tracking-wider">PYQ Papers</p>
            <p className="font-mono text-3xl font-bold mt-1 text-primary">{pyqBank.total || 0}</p>
            <p className="text-xs text-muted-foreground">{pyqBank.years?.length || 0} years</p>
          </CardContent>
        </Card>
      </div>

      {/* Difficulty Legend */}
      <div className="flex items-center gap-6 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-red-500/70" />
          <span className="text-muted-foreground">Hard (UPSC level)</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-amber-500/70" />
          <span className="text-muted-foreground">Medium</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-sm bg-emerald-500/70" />
          <span className="text-muted-foreground">Easy</span>
        </div>
      </div>

      {/* Topic Tree */}
      <div className="space-y-2">
        {data?.topics?.map(topic => (
          <TopicNode key={topic.id} topic={topic} />
        ))}
      </div>
    </div>
  );
}
